import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Gift, Zap, Crown, ShieldCheck, Loader2, Ticket, X, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CardGame } from "@/components/CardGame";
import { useI18n } from "@/context/I18nContext";
import { CreditLedger } from "@/components/CreditLedger";
import { Page, PageHead } from "@/components/hub/Common";
import { HolidayPacks } from "@/components/holiday/HolidayPacks";
import { LimitOfferBanner, useLimitOffer } from "@/components/LimitOffer";

const ICONS = { small: Zap, medium: Coins, large: Crown };

const Stat = ({ icon: Icon, label, value, testid, color }) => (
  <div className="cx-card rounded-2xl p-5" data-testid={testid}>
    <Icon className="w-5 h-5" style={{ color }} />
    <div className="text-3xl font-bold text-white mt-3 font-mono">{value}</div>
    <div className="text-xs text-slate-400 mt-1">{label}</div>
  </div>
);

const PackCard = ({ p, onBuy, busy, disabled, bonus }) => {
  const { t, lang } = useI18n();
  const Icon = ICONS[p.id] || Coins;
  const tag = p.id === "medium" ? t("cr.popular") : p.id === "large" ? t("cr.best") : null;
  const money = (v) => v.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD", currencyDisplay: "narrowSymbol", minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className={`relative cx-card rounded-2xl p-6 flex flex-col ${p.id === "medium" ? "ring-1 ring-[#3ad7ff]/50 shadow-[0_0_40px_-12px_rgba(47,123,255,0.8)]" : ""}`} data-testid={`pack-${p.id}`}>
      {tag && <span className="absolute -top-2.5 right-5 rounded-full bg-gradient-to-r from-[#2f7bff] to-[#a746ff] px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">{tag}</span>}
      <Icon className="w-7 h-7 text-[#3ad7ff]" />
      <div className="text-2xl font-bold text-white mt-4" data-testid={`pack-${p.id}-clips`}>{t("cr.clips").replace("{n}", p.clips)}</div>
      <div className="text-4xl font-black text-white mt-2 font-mono" data-testid={`pack-${p.id}-price`}>{money(p.amount)}<span className="text-sm text-slate-500 font-sans ml-1">CAD</span></div>
      <div className="text-xs text-slate-400 mt-1">{t("cr.per_clip").replace("{p}", money(p.amount / p.clips))}</div>
      {bonus > 0 && <div className="mt-3 inline-flex self-start items-center gap-1.5 rounded-full bg-[#22e3a1]/15 border border-[#22e3a1]/40 px-3 py-1 text-xs font-semibold text-[#22e3a1]" data-testid={`pack-${p.id}-promo-bonus`}><Gift className="w-3.5 h-3.5" />{t("promo.plus").replace("{n}", bonus)}</div>}
      {p.available === false
        ? <div className="mt-6 rounded-full py-3 text-sm font-semibold text-center border border-dashed border-[#ffc94d]/50 text-[#ffc94d] bg-[#ffc94d]/5 flex items-center justify-center gap-2" data-testid={`pack-${p.id}-soon`}><Clock className="w-4 h-4" />{t("cr.soon")}</div>
        : <button onClick={() => onBuy(p.id)} disabled={busy || disabled} data-testid={`pack-${p.id}-buy-btn`}
          className="cx-btn mt-6 rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{t("cr.buy")}
        </button>}
    </div>
  );
};

export default function Credits() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [st, setSt] = useState(null);
  const [packs, setPacks] = useState([]);
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(null);
  const lim = useLimitOffer();
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [checking, setChecking] = useState(false);
  const demo = user?.role === "demo";
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/credits/packs").then((r) => setPacks(r.data));
    api.get("/payments/history").then((r) => setHist(r.data)).catch(() => {});
    if (window.location.hash === "#holiday-packs") {
      const go = (n) => { const el = document.getElementById("holiday-packs"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); else if (n) setTimeout(() => go(n - 1), 300); };
      go(10);
    }
  }, []);
  useEffect(() => {
    const poll = () => api.get("/credits").then((r) => setSt((old) => {
      if (old && (old.credits !== r.data.credits || old.bonus_clips !== r.data.bonus_clips || old.free_remaining !== r.data.free_remaining)) setTick((x) => x + 1);
      return r.data;
    })).catch(() => {});
    poll();
    const id = setInterval(poll, 15000);
    const vis = () => { if (!document.hidden) { poll(); setTick((x) => x + 1); } };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", vis); };
  }, []);
  const buy = async (pack_id) => {
    setBusy(pack_id);
    try {
      const { data } = await api.post("/payments/checkout", { pack_id, origin_url: window.location.origin, promo_code: promo?.code || null });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(null); }
  };
  const [gift, setGift] = useState("");
  const [game, setGame] = useState(null);
  const refresh = () => { api.get("/credits").then((r) => setSt(r.data)); setTick((x) => x + 1); };
  const applyCode = async (raw) => {
    const { data } = await api.post("/promo/check", { code: raw });
    if (data.kind === "bonus") { setPromo(data); setCode(data.code); setGift(""); toast.success(t("promo.ok").replace("{code}", data.code)); return; }
    setGift(""); setCode("");
    if (data.prizes?.length) { setGame(data); return; }
    const r = await api.post("/promo/redeem", { code: data.code });
    toast.success(t("gift.ok").replace("{n}", r.data.clips)); refresh();
  };
  const redeemGift = async (e) => {
    e.preventDefault();
    try { await applyCode(gift); } catch (err) { toast.error(formatApiError(err)); }
  };
  const apply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setChecking(true);
    try { await applyCode(code); } catch (err) { setPromo(null); toast.error(formatApiError(err)); }
    setChecking(false);
  };
  return (
    <Page>
      <div data-testid="credits-page">
      {game && <CardGame game={game} onClose={() => setGame(null)} onWon={refresh} />}
      <PageHead label="CLIP-EX" t1={t("cr.title")} t2="" sub={t("cr.sub")} />
      <div className="mt-10" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat icon={Zap} label={t("cr.free")} value={st ? (st.unlimited ? "∞" : `${st.free_remaining}/${st.free_per_week}`) : "–"} testid="credits-free" color="#3ad7ff" />
        <Stat icon={Gift} label={t("cr.bonus")} value={st ? st.bonus_clips : "–"} testid="credits-bonus" color="#22e3a1" />
        <Stat icon={Coins} label={t("cr.paid")} value={st ? st.credits : "–"} testid="credits-paid" color="#ffc94d" />
      </div>
      {demo && <p className="mt-6 text-sm text-amber-300" data-testid="credits-demo-note">{t("cr.demo")}</p>}
      <form onSubmit={redeemGift} className="mt-8 cx-card rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="gift-form">
        <Gift className="w-5 h-5 text-[#22e3a1]" />
        <span className="text-sm text-white font-semibold">{t("gift.title")}</span>
        <input value={gift} onChange={(e) => setGift(e.target.value.toUpperCase())} placeholder={t("gift.ph")} maxLength={24} disabled={demo}
          data-testid="gift-input" className="bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm text-white font-mono uppercase outline-none focus:border-[#22e3a1]/60 w-full sm:w-52" />
        <button type="submit" disabled={demo || gift.trim().length < 3} data-testid="gift-redeem-btn" className="cx-btn rounded-full px-5 py-2 text-xs font-semibold disabled:opacity-40">{t("gift.redeem")}</button>
      </form>
      <form onSubmit={apply} className="mt-6 flex flex-wrap items-center gap-2" data-testid="promo-form">
        <div className="flex items-center gap-2 rounded-full cx-metal-rail pl-4 pr-1 py-1 w-full sm:w-auto">
          <Ticket className="w-4 h-4 text-[#ffc94d] shrink-0" />
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("promo.ph")} disabled={demo || !!promo} maxLength={24}
            data-testid="promo-input" className="bg-transparent outline-none text-sm text-white font-mono uppercase placeholder:normal-case placeholder:font-sans placeholder:text-slate-500 w-full sm:w-52" />
          {promo
            ? <button type="button" onClick={() => { setPromo(null); setCode(""); }} data-testid="promo-remove-btn" className="cx-metal rounded-full px-3 py-1.5 text-xs flex items-center gap-1"><X className="w-3.5 h-3.5" />{t("promo.remove")}</button>
            : <button type="submit" disabled={demo || checking || !code.trim()} data-testid="promo-apply-btn" className="cx-metal rounded-full px-4 py-1.5 text-xs font-semibold disabled:opacity-40">{checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t("promo.apply")}</button>}
        </div>
        {promo && <span className="text-xs text-[#22e3a1]" data-testid="promo-applied">{t("promo.applied")}</span>}
      </form>
      <LimitOfferBanner offer={lim?.offer} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
        {packs.map((p) => <PackCard key={p.id} p={p} onBuy={buy} busy={busy === p.id} disabled={demo || (busy && busy !== p.id)} bonus={(promo?.bonus?.[p.id] || 0) + (lim?.offer ? Math.round(p.clips * lim.offer.pct / 100) : 0)} />)}
      </div>
      <p className="mt-5 text-xs text-slate-500 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#22e3a1]" />{t("cr.secure")}</p>
      <HolidayPacks demo={demo} onBought={refresh} />
      <h2 className="text-base md:text-lg font-semibold text-white mt-12">{t("cr.history")}</h2>
      <div className="mt-4 space-y-2" data-testid="credits-history">
        {!hist.length && <p className="text-sm text-slate-500">{t("cr.no_history")}</p>}
        {hist.map((h) => (
          <div key={h.session_id} className="cx-card rounded-xl px-4 py-3 flex items-center justify-between text-sm">
            <span className="text-white">{t("cr.clips").replace("{n}", h.clips)}{h.promo_bonus ? <span className="ml-2 text-xs text-[#22e3a1]">+{h.promo_bonus} · {h.promo_code}</span> : null}
              {h.clips_refunded ? <span className="ml-2 text-xs text-[#ff5a8a]" data-testid="history-refunded">{t("cr.refunded").replace("{n}", h.clips_refunded)}</span> : null}</span>
            <span className="text-slate-400 font-mono">{h.amount.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: h.currency })}</span>
            <span className="text-slate-500 text-xs">{new Date(h.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</span>
          </div>
        ))}
      </div>
      <h2 className="text-base md:text-lg font-semibold text-white mt-12">{t("cr.ledger")}</h2>
      <CreditLedger reloadKey={tick} />
      </div>
    </Page>
  );
}
